import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ChallengeCard = ({ challenge }) => {
  const navigate = useNavigate();

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 50) return 'bg-accent';
    return 'bg-primary';
  };

  const formatTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffInHours = Math.ceil((end - now) / (1000 * 60 * 60));
    
    if (diffInHours < 24) return `${diffInHours}h left`;
    const days = Math.ceil(diffInHours / 24);
    return `${days}d left`;
  };

  return (
    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-lg p-6 hover:shadow-elevation-2 transition-gentle">
      {/* Challenge Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-accent text-accent-foreground rounded-full flex items-center justify-center">
            <Icon name="Trophy" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-card-foreground">{challenge?.title}</h3>
            <p className="text-sm text-muted-foreground">{challenge?.category}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-accent">
            {formatTimeRemaining(challenge?.endDate)}
          </p>
          <p className="text-xs text-muted-foreground">
            {challenge?.participants?.toLocaleString()} joined
          </p>
        </div>
      </div>
      {/* Challenge Description */}
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        {challenge?.description}
      </p>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-card-foreground">Progress</span>
          <span className="text-sm text-muted-foreground">{challenge?.progress}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(challenge?.progress)}`}
            style={{ width: `${challenge?.progress}%` }}
          />
        </div>
      </div>
      {/* Goal and Raised */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-card/50 rounded-lg">
          <p className="text-lg font-semibold text-card-foreground">
            ${challenge?.raised?.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Raised</p>
        </div>
        <div className="text-center p-3 bg-card/50 rounded-lg">
          <p className="text-lg font-semibold text-card-foreground">
            ${challenge?.goal?.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">Goal</p>
        </div>
      </div>
      {/* Top Contributors */}
      {challenge?.topContributors && challenge?.topContributors?.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-card-foreground mb-2">Top Contributors</p>
          <div className="flex items-center space-x-2">
            {challenge?.topContributors?.slice(0, 4)?.map((contributor, index) => (
              <div key={contributor?.id} className="relative">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-muted border-2 border-background">
                  <Image
                    src={contributor?.avatar}
                    alt={contributor?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {index === 0 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent text-accent-foreground rounded-full flex items-center justify-center">
                    <Icon name="Crown" size={8} />
                  </div>
                )}
              </div>
            ))}
            {challenge?.topContributors?.length > 4 && (
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-xs font-medium text-muted-foreground">
                +{challenge?.topContributors?.length - 4}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        <Button 
          onClick={() => navigate('/giving-challenges')}
          className="flex-1"
          iconName="Zap"
          iconPosition="left"
        >
          {challenge?.isParticipating ? 'Continue' : 'Join Challenge'}
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/giving-challenges')}
        >
          <Icon name="Users" size={16} />
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => console.log('Share challenge')}
        >
          <Icon name="Share2" size={16} />
        </Button>
      </div>
      {/* Challenge Badge */}
      {challenge?.featured && (
        <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
          Featured
        </div>
      )}
    </div>
  );
};

export default ChallengeCard;